<template>
  <div class="flex flex-col">
    <label :for="field.id" class="custom-file-upload form-input-border-style">
      <FileUploadIcon
        class="mx-auto block h-10 w-10"
        :style="{ color: `#${borderColor}` }"
      />
      <label
        :for="field.id"
        :key="resetKey"
        role="textbox"
        tabindex="0"
        contenteditable="true"
        class="form-placeholder cursor-text text-center shadow-none focus:outline-transparent focus:ring-0"
        :style="{
          color: `#${borderColor}`,
          fontWeight: '600',
          fontSize: '13px',
        }"
        @input="handleLabelInput"
        @compositionstart="handleCompositionStart"
        @compositionend="compositionEndLabelInputHandler"
        @keydown="handleSafariCursor"
        @focus="handleFocus"
        @blur="handleBlur"
        v-text="inputPlaceholder"
      ></label>
      <span
        class="block font-sans text-[11px] font-normal not-italic leading-[12px]"
        :style="{ color: `#${borderColor}` }"
      >
        {{ supportedFileTypes }} {{ maxFileSupported }}
      </span>
    </label>
    <template v-if="isActive">
      <div
        ref="shortLabelElement"
        :key="resetKey"
        role="textbox"
        tabindex="0"
        contenteditable="true"
        class="short-label font-inter cursor-text border-0 bg-transparent p-0 text-sm shadow-none focus:outline-none"
        :data-placeholder="t('common.shortLabelPlaceholder')"
        @blur="updateShortLabel"
        @input="handleInputShortLabel"
        @compositionstart="handleCompositionStart"
        @compositionend="compositionEndHandler"
        @keydown="handleSafariCursor"
        @click="(e) => e.stopPropagation()"
        @focus="(e) => e.stopPropagation()"
        v-text="shortLabel"
      ></div>
    </template>
    <template v-else>
      <span
        v-if="field.shortLabel"
        class="short-label font-inter break-words text-sm"
        >{{ field.shortLabel }}</span
      >
    </template>
  </div>
</template>

<script setup lang="ts">
import { useIMEComposition } from "@/composition/useIMEComposition";
import { useAppStore } from "@/store/app";
import { FileUploadIcon } from "@/util/constants";
import {
  fixSafariCursor,
  handleShortLabelInput as handleShortLabelInputUtil,
} from "@/util/methods";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { handleCompositionStart, handleCompositionEnd, withIMECheck, resetKey } =
  useIMEComposition();

const { t } = useI18n();

const props = defineProps({
  field: {
    type: Object,
    default() {
      return {};
    },
  },
  placeholder: { type: String, default: "" },
  picklistOptions: {
    type: Array,
    default() {
      return [];
    },
  },
  isActive: {
    type: Boolean,
    default: false,
  },
  borderColor: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:placeholder", "update:shortLabel"]);
const inputPlaceholder = ref(props.placeholder);
const shortLabel = ref(props.field.shortLabel || "");
const shortLabelElement = ref<HTMLElement | null>(null);
const store = useAppStore();

const supportedFileTypes = computed(() => {
  const supportedFileTypes = [];
  const picklistOptions = props.field?.picklistOptions;
  if (
    !picklistOptions?.length ||
    (picklistOptions.length === 1 && picklistOptions[0] === "all")
  ) {
    return t("fields.other.fileUploadSupportedFileTypes");
  }

  picklistOptions?.forEach((option) => {
    supportedFileTypes.push(option.split(".").pop().toUpperCase());
  });
  if (supportedFileTypes.length > 1) {
    const lastItem = supportedFileTypes.pop();
    return supportedFileTypes.join(", ") + " or " + lastItem;
  }

  return supportedFileTypes.join(", ");
});

const maxFileSupported = computed(() => {
  return props.field.maxFileSize
    ? ` ( max ${props.field.maxFileSize} ${t("fields.other.fileText")} )`
    : "";
});
// Handler method to call the fixSafariCursor function
const handleSafariCursor = (e: Event) => {
  const isKeyDown = e.type === "keydown";
  fixSafariCursor(e, isKeyDown);
};

watch(
  () => props.placeholder,
  (newVal) => {
    inputPlaceholder.value = newVal;
  }
);

watch(
  () => props.field.shortLabel,
  (newVal) => {
    shortLabel.value = newVal || "";
  }
);

const rawHandleLabelInput = (event: Event) => {
  const target = event.target as HTMLLabelElement;
  inputPlaceholder.value = target.innerText;
  emit("update:placeholder", inputPlaceholder.value);
  // Keep cursor position consistent across browsers (especially Safari)
  handleSafariCursor(event);
};

const handleLabelInput = withIMECheck(rawHandleLabelInput);
const compositionEndLabelInputHandler =
  handleCompositionEnd(rawHandleLabelInput);

const handleFocus = () => {
  // Ensure the label has the placeholder value when focused
  if (!inputPlaceholder.value) {
    inputPlaceholder.value = props.placeholder;
  }
};

const handleBlur = () => {
  store.addToHistory();
};

const updateShortLabel = () => emit("update:shortLabel", shortLabel.value);

const rawInputShortLabelHandler = (e: Event) => {
  handleShortLabelInputUtil(e, shortLabel, updateShortLabel);
};

const handleInputShortLabel = withIMECheck(rawInputShortLabelHandler);
const compositionEndHandler = handleCompositionEnd(rawInputShortLabelHandler);
</script>

<style scoped>
.file-input {
  display: none;
}

.custom-file-upload {
  border-style: solid;
  display: block !important;
  width: 100%;
  min-height: 5rem;
  text-align: center;
  padding: 32px 8px;
}

#_builder-form .custom-file-upload {
  color: #6e7d84 !important;
}

.bi-upload {
  font-size: 20px;
}

.short-label {
  background-color: transparent;
  margin-top: 4px;
  font-size: 14px;
  color: #4b5563;
}

.short-label:empty:before {
  content: attr(data-placeholder);
}

[contenteditable="true"] {
  min-height: 24px;
  display: inline-block;
  width: 100%;
  outline: none;
  background-color: transparent;
  border: none;
}

.form-placeholder {
  content: attr(data-placeholder);
  line-height: 18px;
  letter-spacing: 0;
  font-family: Inter;
}

[contenteditable="true"]:empty:before {
  content: attr(data-placeholder);
  color: #aaa;
  pointer-events: none;
}
</style>
